import { Session } from 'next-auth'
import { signIn, useSession } from 'next-auth/react'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { setUserPassword } from '@/api/settings/setUserPhone'
import { verifyUserPhone } from '@/api/settings/verifyUserPhone'
import PhoneTextField from '@/components/ui/PhoneTextField'
import Spinner from '@/components/ui/Spinner'
import TextField from '@/components/ui/TextField'
import ButtonText from '@/components/ui/Typo/ButtonText'
import Subtitle from '@/components/ui/Typo/Subtitle'
import { IconReload } from '@/res/icons'
import { NAMESPACE_SETTINGS_PAGE } from '@/res/namespaces'
import { transformUserObject } from '@/utils/helpers'

interface IForm {
  phone_number: string
  security_code?: string
}

const AddPhoneNumberForm: React.FC = () => {
  const { t } = useTranslation(NAMESPACE_SETTINGS_PAGE)
  const { data } = useSession()
  const [sessionToken, setSessionToken] = useState('')
  const [pending, setPending] = useState(false)
  const [showChangeBtn, setShowChangeBtn] = useState(false)
  const [seconds, setSeconds] = useState(60)
  const [timerActive, setTimerActive] = useState(true)

  const currentPhoneNumber = data?.user.phone_number?.substring(1)
  const defaultValues = {
    phone_number: '',
    security_code: '',
  }

  const {
    setValue,
    watch,
    formState: { touchedFields, errors },
    handleSubmit,
    setError,
    register,
  } = useForm<IForm>({
    defaultValues,
  })

  const phoneValue = watch('phone_number')

  const phoneError = errors.phone_number
  const codeError = errors.security_code

  const onSuccess = () => toast.success(t('phone_change_success'))

  useEffect(() => {
    if (
      touchedFields.phone_number &&
      currentPhoneNumber !== phoneValue &&
      phoneValue.length === 12
    ) {
      setShowChangeBtn(true)
    } else {
      setShowChangeBtn(false)
    }
  }, [phoneValue, currentPhoneNumber, touchedFields.phone_number])

  const onSubmit = handleSubmit(async ({ phone_number, security_code }) => {
    try {
      setPending(true)
      if (!sessionToken) {
        const res = await setUserPassword({ phone_number: `+${phone_number}` })
        setTimerActive(true)
        setSessionToken(res.session_token)
      } else if (security_code) {
        await verifyUserPhone({ security_code, session_token: sessionToken })
        const session: Session = {
          expires: data?.expires as string,
          user: {
            ...(data?.user as any),
            phone_number: `+${phone_number}`,
          },
        }

        const user = transformUserObject(session)
        signIn('credentials', {
          user: JSON.stringify(user),
          redirect: false,
        })
        onSuccess()
        setSessionToken('')
        setShowChangeBtn(false)
      }
    } catch (err: any) {
      const errors = err.response?.data?.errors || {}

      Object.keys(errors).forEach((fieldErr) => {
        setError(fieldErr as keyof IForm, {
          message: errors[fieldErr],
        })
      })
    } finally {
      setPending(false)
    }
  })

  useEffect(() => {
    if (seconds > 0 && timerActive) {
      setTimeout(setSeconds, 1000, seconds - 1)
    } else {
      setTimerActive(false)
    }
  }, [seconds, timerActive])

  const phone = watch('phone_number')
  const handleSubmitRetry = async () => {
    const res = await setUserPassword({ phone_number: `+${phone}` })
    setSeconds(60)
    setTimerActive(true)
    setSessionToken(res.session_token)
  }

  return (
    <div className="rounded-lg border border-gray-300 mt-10 py-7 small:px-10 px-5">
      <Subtitle>
        {currentPhoneNumber ? t('change_phone_number') : t('set_phone_number')}
      </Subtitle>
      <form onSubmit={onSubmit} className="mt-8">
        <div
          className={`flex items-start justify-start w-full ${
            sessionToken ? 'flex-wrap' : 'tablet:flex-nowrap flex-wrap'
          }`}
        >
          {/* <div className="flex"> */}
          <PhoneTextField
            label={t('phone_number')}
            wrapperClassName="max-w-xs tablet:mr-4"
            onChange={(v) => {
              setValue('phone_number', v, {
                shouldTouch: true,
              })
            }}
            value={phoneValue || currentPhoneNumber || ''}
            name="phone_number"
            required
            error={phoneError}
            //eslint-disable-next-line
            autoFocus={!data?.user.phone_number}
          />
          {sessionToken && (
            <div className="w-full flex items-center max-w-xs space-x-4 desktop:ml-5">
              <TextField
                label={t('security_code')}
                wrapperClassName="max-w-xs h-[77px]"
                inputProps={{
                  ...register('security_code'),
                  required: true,
                  autoFocus: true,
                }}
                error={codeError}
              />
              <div className="h-20 mt-5">
                {timerActive ? (
                  <div className="rounded-full flex justify-center items-center h-8 w-8 bg-[#E8F0FE]">
                    {seconds}
                  </div>
                ) : (
                  <div role="button" onClick={() => handleSubmitRetry()}>
                    <img className="h-8 w-8" src={IconReload.src} alt="" />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {showChangeBtn && (
          <div className="flex tablet:justify-end w-full mt-3">
            <button
              type="submit"
              className="rounded-lg max-w-xs w-full py-3 bg-primary hover:opacity-90 active:scale-x-95 transition-all"
            >
              {pending ? (
                <span className="flex justify-center w-full">
                  <Spinner />
                </span>
              ) : (
                <ButtonText size="small">{t('save')}</ButtonText>
              )}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}

export default AddPhoneNumberForm
